import ReactFlow, { Background, Controls } from 'reactflow';
import { Box, Button, Paper, styled, Stack, IconButton, Divider, SxProps, Theme } from '@mui/material';
import { Close, Dialpad, NearMe, PanTool, PhoneForwarded, Save } from '@mui/icons-material';

import { CallsMenuStageType } from '@bandido/shared/constants';

import { useSidebar } from '@frontend/features/dashboard/contexts/SidebarContext/hook';
import {
  closedSidebarWidthRem,
  openSidebarWidthRem,
} from '@frontend/features/dashboard/components/DashboardSidebar/constants';

import { useCallsMenu } from '../CallsMenuContext';

import { edgeTypes, stageTypes } from '../constants';
import { handleDragStart, onDragOver } from './Stage/utils';

import 'reactflow/dist/style.css';

export const containerStyles = (disabled?: boolean): SxProps<Theme> => ({
  border: (theme) => `1px solid ${theme.palette.divider}`,
  borderRadius: 1,
  width: 60,
  height: 61,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxSizing: 'border-box',
  cursor: disabled ? 'not-allowed' : 'grab',
  transition: 'all 0.2s ease-in-out',
});

const WorkflowActionButton = styled(Button)`
  min-width: 48px;
  width: 48px;
  height: 48px;
`;

export const Diagram = () => {
  const {
    stages,
    edges,
    onConnect,
    onNodesChange,
    onEdgesChange,
    onCanvasClick,
    onCreateStage,
    onSave,
    isValidConnection,
  } = useCallsMenu();

  const { isOpen } = useSidebar();

  const sidebarWidth = isOpen ? openSidebarWidthRem : closedSidebarWidthRem;

  return (
    <Box width={`calc(100vw - ${sidebarWidth}rem)`} height="calc(100vh - 64px)">
      <ReactFlow
        fitView
        nodes={stages}
        edges={edges}
        onClick={onCanvasClick}
        onDrop={onCreateStage}
        onDragOver={onDragOver}
        isValidConnection={isValidConnection}
        edgeTypes={edgeTypes}
        nodeTypes={stageTypes}
        onConnect={onConnect}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
      >
        <Controls />
        <Background />
        <Paper
          elevation={4}
          sx={{
            left: '35%',
            transform: 'translateX(-35%)',
            backgroundColor: (theme) => theme.palette.common.white,
            zIndex: 4,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            bottom: 24,
            position: 'absolute',
          }}
        >
          <Box gap={2} display="flex" flexDirection="row" alignItems="center" alignSelf="stretch">
            <Stack direction="row">
              <Box display="flex" flexDirection="column">
                <IconButton sx={{ p: 1.5 }}>
                  <NearMe />
                </IconButton>
                <Divider />
                <IconButton sx={{ p: 1.5 }}>
                  <PanTool />
                </IconButton>
              </Box>
              <Divider orientation="vertical" flexItem />
            </Stack>
            <Stack gap={2} flexDirection="row">
              <Box
                draggable
                onDragStart={(event) => handleDragStart(event, CallsMenuStageType.SUB_MENU)}
                sx={containerStyles()}
              >
                <Dialpad sx={{ color: (theme) => theme.palette.primary.light }} />
              </Box>
              <Box
                draggable
                onDragStart={(event) => handleDragStart(event, CallsMenuStageType.ACTION)}
                sx={containerStyles()}
              >
                <PhoneForwarded sx={{ color: (theme) => theme.palette.success.main }} />
              </Box>
            </Stack>
            <Divider orientation="vertical" flexItem />
            <Stack gap={1} padding={(theme) => theme.spacing(2.5, 3, 2.5, 1)} direction="row">
              <WorkflowActionButton variant="outlined" color="error">
                <Close />
              </WorkflowActionButton>
              <WorkflowActionButton onClick={onSave} variant="outlined" color="primary">
                <Save />
              </WorkflowActionButton>
            </Stack>
          </Box>
        </Paper>
      </ReactFlow>
    </Box>
  );
};
